import {ChangeDetectionStrategy, Component, computed, input, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {interval, Observable} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {IDataTimeStamps} from '../../../../../features/core/models/data-time-stamps';
import {IStore} from '../../../../shared-models/store/store';

const FRESHNESS_TICK_INTERVAL_MS = 30000;

export const FRESHNESS_COLOUR_LIVE = '#00C950FF';
export const FRESHNESS_COLOUR_RECENT = 'lightgreen';
export const FRESHNESS_COLOUR_AGEING = 'orange';
export const FRESHNESS_COLOUR_STALE = 'red';
export const FRESHNESS_COLOUR_UNKNOWN = 'grey';

/**
 * Shared clock that ages every indicator on screen. Subscribers share a single timer, and the emitted
 * value is only used to invalidate the derived colour and label.
 */
const freshnessTick$: Observable<number> = interval(FRESHNESS_TICK_INTERVAL_MS).pipe(
  shareReplay({bufferSize: 1, refCount: true}),
);

@Component({
  selector: 'app-shared-freshness-indicator-component',
  templateUrl: './shared-freshness-indicator-component.component.html',
  styleUrls: ['./shared-freshness-indicator-component.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedFreshnessIndicatorComponentComponent {
  readonly dataType = input<string>('stock');
  readonly currentStore = input<IStore>();
  readonly timeStamps = input<Record<string, IDataTimeStamps>>();

  private readonly tick: Signal<number> = toSignal(freshnessTick$, {initialValue: 0});

  /**
   * Timestamp for the store and data type currently being displayed. Derived rather than cached so that
   * switching store or data type re-reads the timestamp instead of keeping the previous store's value.
   */
  private readonly dataTimeStamp: Signal<Date | null> = computed(() => {
    const storeId = this.currentStore()?.storeId;
    const dataType = this.dataType() as keyof IDataTimeStamps;

    if (!storeId || !dataType) {
      return null;
    }

    const timeStamp = this.timeStamps()?.[storeId]?.[dataType];
    if (!timeStamp) {
      return null;
    }

    const timeStampDate = new Date(timeStamp);
    return isNaN(timeStampDate.getTime()) ? null : timeStampDate;
  });

  private readonly ageInMinutes: Signal<number | null> = computed(() => {
    void this.tick();

    const timeStampDate = this.dataTimeStamp();
    if (!timeStampDate) {
      return null;
    }

    return (Date.now() - timeStampDate.getTime()) / 60000;
  });

  protected readonly colour: Signal<string> = computed(() => {
    const ageInMinutes = this.ageInMinutes();
    if (ageInMinutes === null) {
      return FRESHNESS_COLOUR_UNKNOWN;
    }

    if (this.dataType() !== 'stock') {
      if (ageInMinutes < 6) {
        return FRESHNESS_COLOUR_LIVE;
      }
      return ageInMinutes < 60 ? FRESHNESS_COLOUR_AGEING : FRESHNESS_COLOUR_STALE;
    }

    if (ageInMinutes < 2) {
      return FRESHNESS_COLOUR_LIVE;
    }
    if (ageInMinutes < 30) {
      return FRESHNESS_COLOUR_RECENT;
    }
    return ageInMinutes < 70 ? FRESHNESS_COLOUR_AGEING : FRESHNESS_COLOUR_STALE;
  });

  protected readonly timeSinceUpdate: Signal<string> = computed(() => {
    const ageInMinutes = this.ageInMinutes();
    if (ageInMinutes === null) {
      return 'No update information available.';
    }

    const minutes = Math.floor(ageInMinutes);

    if (minutes < 1) {
      return 'Data Is Live';
    }

    if (minutes < 60) {
      return `Updated ${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }

    if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `Updated ${hours} hour${hours > 1 ? 's' : ''} ago`;
    }

    if (minutes < 10080) {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      const dayText = days > 1 ? 'days' : 'day';
      const hourText = hours > 1 ? 'hours' : 'hour';
      return `Updated ${days} ${dayText}${hours > 0 ? ' and ' + hours + ' ' + hourText : ''} ago`;
    }

    const weeks = Math.floor(minutes / 10080);
    const days = Math.floor((minutes % 10080) / 1440);
    const weekText = weeks > 1 ? 'weeks' : 'week';
    const dayText = days > 1 ? 'days' : 'day';
    return `Updated ${weeks} ${weekText}${days > 0 ? ' and ' + days + ' ' + dayText : ''} ago`;
  });
}
